import { LeadScheduleInputs } from "@/components/common/LeadScheduleInputs";
import { Entries } from "@/types/utility";
import { entriesToRecord } from "@/utils/array-utils";
import { DAYS, WEEKDAYS } from "@/utils/dates";
import { Control, FieldPathByValue, FieldValues } from "react-hook-form";
import z from "zod";
import { SegmentHeader } from "../../basic/SegmentHeader";

const campaignSchedulesFieldsSchema = z.object(
  entriesToRecord(WEEKDAYS.map((day) => [day.id, z.number().nullable()])),
);

export const createCampaignSchedulesFieldsSchema = (
  defaultValues?: Partial<Record<(typeof DAYS)[number]["id"], number | null>>,
) =>
  campaignSchedulesFieldsSchema
    .refine((schedules) => {
      const entries = Object.entries(schedules) as Entries<typeof schedules>;
      return entries.some(([dayId, amount]) => {
        amount ??= defaultValues?.[dayId] ?? 0;
        return amount > 0;
      });
    }, "Du skal vælge mindst én dag med leads")
    .refine((schedules) => {
      const entries = Object.entries(schedules) as Entries<typeof schedules>;
      return entries.every(([dayId, amount]) => {
        amount ??= defaultValues?.[dayId] ?? 0;
        return amount >= 0 && amount <= 1000;
      });
    }, "Antallet af leads pr. dag skal være mellem 0 og 1000");

export function CampaignSchedulesField<T extends FieldValues>({
  control,
  name,
  defaultValues,
}: {
  control: Control<T>;
  name: FieldPathByValue<
    T,
    Partial<Record<(typeof WEEKDAYS)[number]["id"], number | null>> | undefined
  >;
  defaultValues?: Partial<
    Record<(typeof WEEKDAYS)[number]["id"], number | null>
  >;
}) {
  return (
    <div className="flex w-full flex-col items-center gap-y-10">
      <SegmentHeader
        className="max-w-lg"
        title="Antal leads"
        description="Her skal du indtaste det antal leads, du ønsker at modtage hver uge. Du vælger selv, hvor mange du ønsker at modtage per dag, mandag til fredag. Dine leads leveres inden kl. 8 på de valgte dage."
      />

      <div className="max-w-160">
        <LeadScheduleInputs
          control={control}
          name={name}
          placeholders={defaultValues}
        />
      </div>
    </div>
  );
}
